/* eslint-disable */
import { useState, useCallback, useEffect, useRef } from 'react';
import { MULTICHOIX_SEARCH_STEPS, MULTICHOIX_STEP_STATUS } from '../constants/multichoixSteps';

/**
 * Hook to manage multichoix step wizard state
 * Handles step progression, validation, and multi-select state
 */
export const useMultichoixStepManagement = (initialSearchData = null) => {
  // Track if we've already initialized to prevent re-initialization on prop changes
  const isInitializedRef = useRef(false);

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [selections, setSelections] = useState({});
  const [selectionLabels, setSelectionLabels] = useState({});

  // Initialize from existing search data if provided (only once on mount)
  useEffect(() => {
    // Only initialize once - subsequent changes to initialSearchData are ignored
    // because the component manages its own state after initialization
    if (initialSearchData && !isInitializedRef.current) {
      isInitializedRef.current = true;

      const newSelections = { ...initialSearchData.selections };
      const newLabels = { ...initialSearchData.selectionLabels };
      const newCompletedSteps = new Set();

      MULTICHOIX_SEARCH_STEPS.forEach((step) => {
        const fieldValues = newSelections[step.field];
        if (fieldValues && fieldValues.length > 0) {
          newCompletedSteps.add(step.key);
        }
      });

      setSelections(newSelections);
      setSelectionLabels(newLabels);
      setCompletedSteps(newCompletedSteps);

      // Find the first incomplete required step or go to first step
      const firstIncomplete = MULTICHOIX_SEARCH_STEPS.find(
        (step) => !newCompletedSteps.has(step.key) && isStepRequiredInternal(step.key, newSelections, newLabels)
      );
      setCurrentStep(firstIncomplete?.key || 1);
    }
  }, [initialSearchData]);

  // Update selectionLabels when enriched data arrives (cold start label restoration)
  // This runs after initialization to fill in labels that were fetched asynchronously
  useEffect(() => {
    if (!initialSearchData?.selectionLabels || !isInitializedRef.current) {
      return;
    }

    setSelectionLabels((prevLabels) => {
      const newLabels = { ...prevLabels };
      let hasChanges = false;

      // For each field, update labels if we have selections but no labels
      MULTICHOIX_SEARCH_STEPS.forEach((step) => {
        const fieldSelections = selections[step.field];
        const existingLabels = prevLabels[step.field];
        const enrichedLabels = initialSearchData.selectionLabels[step.field];

        // Only update if:
        // 1. We have selections for this field
        // 2. We don't have labels yet (or labels are empty)
        // 3. Enriched labels are available
        if (
          fieldSelections?.length > 0 &&
          (!existingLabels || existingLabels.length === 0) &&
          enrichedLabels?.length > 0
        ) {
          newLabels[step.field] = enrichedLabels;
          hasChanges = true;
        }
      });

      return hasChanges ? newLabels : prevLabels;
    });
  }, [initialSearchData?.selectionLabels, selections]);

  const isStepRequiredInternal = useCallback((stepKey, currentSelections, currentLabels) => {
    const step = MULTICHOIX_SEARCH_STEPS.find((s) => s.key === stepKey);
    if (!step) return false;

    if (typeof step.required === 'function') {
      return step.required({
        ...currentSelections,
        _labels: currentLabels,
      });
    }
    return step.required;
  }, []);

  const isStepRequired = useCallback((stepKey) => {
    return isStepRequiredInternal(stepKey, selections, selectionLabels);
  }, [selections, selectionLabels, isStepRequiredInternal]);

  const isStepValid = useCallback((stepKey) => {
    const step = MULTICHOIX_SEARCH_STEPS.find((s) => s.key === stepKey);
    if (!step) return false;

    const stepSelections = selections[step.field];
    const hasSelections = Array.isArray(stepSelections) && stepSelections.length > 0;
    const stepRequired = isStepRequired(stepKey);

    // If step is required, it needs selections to be valid
    // If step is not required, it's always valid (can be skipped)
    return hasSelections || !stepRequired;
  }, [selections, isStepRequired]);

  const areAllRequiredStepsValid = useCallback(() => {
    return MULTICHOIX_SEARCH_STEPS.every((step) => {
      if (!isStepRequired(step.key)) return true;
      const stepSelections = selections[step.field];
      return Array.isArray(stepSelections) && stepSelections.length > 0;
    });
  }, [selections, isStepRequired]);

  const canAccessStep = useCallback((stepKey) => {
    // Step 1 is always accessible
    if (stepKey === 1) return true;

    // For other steps, check if all previous required steps are completed
    for (let i = 1; i < stepKey; i++) {
      const prevStep = MULTICHOIX_SEARCH_STEPS.find((s) => s.key === i);
      if (prevStep && isStepRequired(i)) {
        const prevSelections = selections[prevStep.field];
        if (!prevSelections || prevSelections.length === 0) {
          return false;
        }
      }
    }
    return true;
  }, [selections, isStepRequired]);

  const getStepStatus = useCallback((stepKey) => {
    if (completedSteps.has(stepKey)) {
      return MULTICHOIX_STEP_STATUS.COMPLETED;
    }
    if (stepKey === currentStep) {
      return MULTICHOIX_STEP_STATUS.ACTIVE;
    }
    if (canAccessStep(stepKey)) {
      return MULTICHOIX_STEP_STATUS.AVAILABLE;
    }
    return MULTICHOIX_STEP_STATUS.DISABLED;
  }, [completedSteps, currentStep, canAccessStep]);

  const handleStepChange = useCallback((stepKey, values, labelObjects = []) => {
    const step = MULTICHOIX_SEARCH_STEPS.find((s) => s.key === stepKey);
    if (!step) return;

    // Mark as initialized when user makes their first selection
    // This prevents the initialization effect from overwriting user selections
    // with empty data when URL state triggers a re-render
    if (!isInitializedRef.current && values && values.length > 0) {
      isInitializedRef.current = true;
    }

    // Get all steps that come after the current step
    const stepsToReset = MULTICHOIX_SEARCH_STEPS.filter((s) => s.key > stepKey);

    // Update selections: set current step value and clear all subsequent steps
    setSelections((prev) => {
      const newSelections = { ...prev, [step.field]: values };
      stepsToReset.forEach((s) => {
        newSelections[s.field] = [];
      });
      return newSelections;
    });

    // Update labels: set current step labels and clear all subsequent steps
    setSelectionLabels((prev) => {
      const newLabels = { ...prev, [step.field]: labelObjects };
      stepsToReset.forEach((s) => {
        newLabels[s.field] = [];
      });
      return newLabels;
    });

    // Update completed steps
    setCompletedSteps((prev) => {
      const newSet = new Set(prev);
      // Remove all subsequent steps from completed
      stepsToReset.forEach((s) => {
        newSet.delete(s.key);
      });
      // Add or remove current step based on whether it has values
      if (values && values.length > 0) {
        newSet.add(stepKey);
      } else {
        newSet.delete(stepKey);
      }
      return newSet;
    });
  }, []);

  // Relabel the current step only (e.g. enemy display language): no cascade reset.
  const updateStepLabels = useCallback((stepKey, labelObjects) => {
    const step = MULTICHOIX_SEARCH_STEPS.find((s) => s.key === stepKey);
    if (!step) return;
    setSelectionLabels((prev) => ({ ...prev, [step.field]: labelObjects }));
  }, []);

  const handleStepClick = useCallback((stepKey) => {
    if (canAccessStep(stepKey)) {
      setCurrentStep(stepKey);
    }
  }, [canAccessStep]);

  const handleStepSkip = useCallback((stepKey) => {
    if (stepKey < MULTICHOIX_SEARCH_STEPS.length) {
      setCurrentStep(stepKey + 1);
    }
  }, []);

  const handleReset = useCallback(() => {
    setCurrentStep(1);
    setCompletedSteps(new Set());
    setSelections({});
    setSelectionLabels({});
  }, []);

  const forceStepRevalidation = useCallback(() => {
    // Force re-render by updating state
    setSelections((prev) => ({ ...prev }));
  }, []);

  return {
    currentStep,
    completedSteps,
    selections,
    selectionLabels,
    setCurrentStep,
    isStepValid,
    isStepRequired,
    areAllRequiredStepsValid,
    canAccessStep,
    getStepStatus,
    handleStepChange,
    updateStepLabels,
    handleStepSkip,
    handleStepClick,
    handleReset,
    forceStepRevalidation,
  };
};

export default useMultichoixStepManagement;
